import React, { useState } from 'react';
import { useUser } from '../../hooks/UserContext';
import { auth } from '../../firebase';

// A simple, reusable Modal component
const Modal = ({ show, onClose, title, children }) => {
  if (!show) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg">
        <h3 className="text-xl font-semibold mb-4">{title}</h3>
        <div>{children}</div>
        <div className="mt-6 flex justify-end">
          {onClose && (
             <button 
              onClick={onClose} 
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded"
            >
              閉じる
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const CATEGORIES = [
  "サービスについて",
  "契約内容・利用料金",
  "操作方法",
  "各種手続き（プラン変更・解約・ご登録内容の変更）",
  "不具合報告",
  "障害・エラートラブル",
  "ご要望",
  "その他",
];

const ContactPage = () => {
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Modal states
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [ticketNo, setTicketNo] = useState('');
  const [error, setError] = useState('');

  const { user, loading: userLoading } = useUser();

  const validate = () => {
    if (!category || !CATEGORIES.includes(category)) {
      setError('正しいお問い合わせの種類を選択してください。');
      return false;
    }
    if (!content.trim()) {
      setError('お問い合わせ内容を入力してください。');
      return false;
    }
    if (content.length > 5000) {
      setError('お問い合わせ内容は5000文字以内で入力してください。');
      return false;
    }
    return true;
  };

  const handleConfirm = () => {
    setError('');
    if (!validate()) {
      return;
    }
    setConfirmModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!auth.currentUser) {
        setError("ログインしていません。ログインし直してから再度お試しください。");
        setConfirmModalOpen(false);
        return;
    }

    setIsLoading(true);
    setConfirmModalOpen(false);

    try {
        const token = await auth.currentUser.getIdToken();
        
        const response = await fetch('/v1/contact', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ category, content }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || `Server responded with ${response.status}`);
        }

        setTicketNo(data.ticketNo || '');
        setSuccessModalOpen(true);
        setContent(''); // Reset form
        setCategory(CATEGORIES[0]);
    } catch (err) {
        console.error("Error sending inquiry:", err);
        setError(err.message || "送信に失敗しました。時間をおいてから再度お試しください。");
    } finally {
        setIsLoading(false);
    }
  };

  if (userLoading) {
      return <div>読み込み中...</div>;
  }

  return (
    <div className="bg-white p-8 w-full max-w-4xl mx-auto my-8 shadow-lg rounded-lg">
      <h1 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-gray-200">
        お問い合わせ
      </h1>

      <p className="text-gray-700 mb-6">
        お問い合わせの受付は24時間365日行っておりますが、回答は平日（土日祝日及び弊社指定休日を除く）の9:00-17:00となっております。<br />
        夜間、休日等に受領したお問い合わせについては翌営業日以降の対応となりますのでご了承ください。
      </p>

      <div className="space-y-6">
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">お問い合わせの種類</label>
          <select 
            id="category" 
            value={category} 
            onChange={(e) => setCategory(e.target.value)}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
          >
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
            お問い合わせ内容 <span className="text-gray-400 font-normal">(5000文字以内)</span>
          </label>
          <textarea 
            id="content" 
            rows={8} 
            maxLength={5000}
            value={content} 
            onChange={(e) => setContent(e.target.value)}
            className="mt-1 block w-full p-2 text-base border border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md shadow-sm"
            placeholder="具体的なお問い合わせ内容をご記入ください。"
          />
          <div className="flex justify-end mt-1">
            <span className="text-xs text-gray-400">
              {content.length} / 5000文字
            </span>
          </div>
        </div>

        {error && <p className="text-red-500 text-sm">{error}</p>}

        <div className="text-center">
          <button 
            onClick={handleConfirm} 
            disabled={isLoading}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
          >
            {isLoading ? '送信中...' : '入力内容を確認する'}
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {isConfirmModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg">
                <h3 className="text-xl font-semibold mb-4">入力内容の確認</h3>
                <div className="space-y-4">
                    <div>
                        <p className="font-semibold">お問い合わせの種類:</p>
                        <p>{category}</p>
                    </div>
                    <div>
                        <p className="font-semibold">お問い合わせ内容:</p>
                        <p className="whitespace-pre-wrap">{content}</p>
                    </div>
                </div>
                <div className="mt-6 flex justify-end space-x-4">
                    <button onClick={() => setConfirmModalOpen(false)} className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded">
                        戻る
                    </button>
                    <button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                        送信する
                    </button>
                </div>
            </div>
        </div>
      )}

      {/* Success Modal */}
      <Modal 
        show={isSuccessModalOpen} 
        onClose={() => setSuccessModalOpen(false)}
        title="送信完了"
      >
        <p className="text-gray-700 mb-4">お問い合わせを受け付けました。</p>
        {ticketNo && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 my-4 text-center">
            <p className="text-xs text-gray-500 mb-1">チケット番号</p>
            <p className="text-2xl font-mono font-bold text-blue-600 tracking-wider">{ticketNo}</p>
          </div>
        )}
        <p className="text-xs text-gray-500">ご登録のメールアドレス宛に自動返信メールをお送りいたしました。</p>
      </Modal>
    </div>
  );
};

export default ContactPage;
